import React, { useState } from 'react';
import AddToHomePrompt from '../components/AddToHomePrompt';
import TileGrid from '../components/TileGrid';
import ContactView from '../components/ContactView';
import TenantBrandLockup from '../brand/TenantBrandLockup';
import { FIRELINE_MEDIC_BADGE } from '../brand/firelineMedicBadge';
import RadioReference from '../radio/RadioReference';
import SacCountyProtocols from '../protocols/SacCountyProtocols';
import DrugCalculator from '../calculator/DrugCalculator';
import FireWeather from '../weather/FireWeather';
import SavedIaps from '../iap/SavedIaps';
import { supabase } from '../auth/supabase';
import '../index.css';
import '../theme.css';

const CONTACTS_SHEET_ID = '1WE_XlmvV8eYEGy0HMmMjhsN8F2xvfe6ht9xfWi0vxxY';
const THEME_STORAGE_KEY = 'smfd-line-medic-theme';

function expiryText(value) {
  const date = new Date(value || '');
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

function initialTheme() {
  if (typeof window === 'undefined') return 'light';
  const saved = window.localStorage?.getItem(THEME_STORAGE_KEY);
  if (saved === 'dark' || saved === 'light') return saved;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#09100e' : '#edf2f4');
}

function ThemeToggle() {
  const [theme, setTheme] = useState(() => initialTheme());

  const toggle = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
    if (typeof window !== 'undefined') window.localStorage?.setItem(THEME_STORAGE_KEY, next);
  };

  return (
    <button
      className="smfd-theme-toggle"
      type="button"
      onClick={toggle}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      <span className="smfd-theme-toggle-icon" aria-hidden="true">{theme === 'dark' ? '☾' : '☀'}</span>
      <span className="smfd-theme-toggle-label">{theme === 'dark' ? 'Dark' : 'Light'}</span>
    </button>
  );
}

function RemsHeader({ profile, onSignOut }) {
  return (
    <header className="smfd-brand-header">
      <TenantBrandLockup profile={profile} className="smfd-brand-lockup" />
      <div className="smfd-account-actions">
        <div className="smfd-account-pill">
          <small>Signed in as</small>
          <strong>{profile?.full_name || 'REMS Member'}</strong>
        </div>
        <button className="smfd-signout" type="button" onClick={() => window.location.assign('/settings')}>Settings</button>
        <ThemeToggle />
        <button className="smfd-signout" type="button" onClick={onSignOut}>Sign out</button>
      </div>
    </header>
  );
}

function RemsSubpage({ profile, onBack, onSignOut, kicker, title, subtitle, children }) {
  return (
    <>
      <AddToHomePrompt />
      <div className="app-container">
        <RemsHeader profile={profile} onSignOut={onSignOut} />
        <button type="button" className="back-button" onClick={onBack}>← REMS Home</button>
        <div className="dashboard-intro">
          <div className="dashboard-intro-copy">
            <span className="dashboard-eyebrow">{kicker}</span>
            <h2>{title}</h2>
            <p>{subtitle}</p>
          </div>
          <div className="dashboard-access-badge">REMS access active</div>
        </div>
        <section className="dashboard-subpage">{children}</section>
      </div>
    </>
  );
}

export default function RemsDashboard({ profile }) {
  const [view, setView] = useState('home');
  const temporary = profile?.member_kind === 'rems_temporary';

  const signOut = async () => {
    await supabase.auth.signOut();
    window.location.assign('/');
  };

  if (view === 'radio') {
    return (
      <RemsSubpage
        profile={profile}
        onSignOut={signOut}
        onBack={() => setView('home')}
        kicker="FIELD REFERENCE"
        title="Radio Reference"
        subtitle="Offline-ready CAL FIRE KNG statewide bank and channel reference."
      >
        <RadioReference />
      </RemsSubpage>
    );
  }

  if (view === 'protocols') {
    return (
      <RemsSubpage
        profile={profile}
        onSignOut={signOut}
        onBack={() => setView('home')}
        kicker="FIELD REFERENCE"
        title="Sac County Protocols"
        subtitle="Searchable, offline-ready official Sacramento County EMS policies, procedures and protocols."
      >
        <SacCountyProtocols />
      </RemsSubpage>
    );
  }

  if (view === 'iaps') {
    return (
      <RemsSubpage
        profile={profile}
        onSignOut={signOut}
        onBack={() => setView('home')}
        kicker="FIELD REFERENCE"
        title="Saved IAPs"
        subtitle="Scan the physical IAP QR, choose the actual PDF, then keep a searchable offline copy on this device."
      >
        <SavedIaps />
      </RemsSubpage>
    );
  }

  if (view === 'drug-calculator') {
    return (
      <RemsSubpage
        profile={profile}
        onSignOut={signOut}
        onBack={() => setView('home')}
        kicker="MEDICATION CALCULATOR"
        title="Drug Calculator"
        subtitle="Offline Sacramento County adult and pediatric weight-based dosing, carried concentrations, and draw volumes."
      >
        <DrugCalculator onOpenProtocols={() => setView('protocols')} />
      </RemsSubpage>
    );
  }

  if (view === 'fire-weather') {
    return (
      <RemsSubpage
        profile={profile}
        onSignOut={signOut}
        onBack={() => setView('home')}
        kicker="FIRE WEATHER"
        title="Fire Weather — My Location"
        subtitle="Official NWS digital fire-weather data for the phone’s approximate location, cached for field use."
      >
        <FireWeather />
      </RemsSubpage>
    );
  }

  const operationalTiles = [
    {
      id: 'my-gear',
      title: 'My Gear',
      description: 'Deploy, scan, track and return accountable REMS gear.',
      logo: FIRELINE_MEDIC_BADGE,
      variant: 'primary',
      onClick: () => window.location.assign('/my-gear'),
    },
    {
      id: 'ics-214',
      title: 'ICS 214',
      description: 'Build daily activity logs offline and email the completed 214 to the MEDL.',
      icon: 'document',
      variant: 'primary',
      onClick: () => window.location.assign('/ics214'),
    },
    {
      id: 'radio-reference',
      title: 'Radio Reference',
      description: 'Quick field guides and radio reference material.',
      icon: 'radio',
      variant: 'reference',
      onClick: () => setView('radio'),
    },
    {
      id: 'sac-protocols',
      title: 'Sac County Protocols',
      description: 'Search official County EMS protocols with offline access.',
      icon: 'document',
      variant: 'reference',
      onClick: () => setView('protocols'),
    },
    {
      id: 'saved-iaps',
      title: 'Saved IAPs',
      description: 'Scan, choose and save incident IAP PDFs for offline field reference.',
      icon: 'document',
      variant: 'reference',
      onClick: () => setView('iaps'),
    },
    {
      id: 'drug-calculator',
      title: 'Drug Calculator',
      description: 'Adult + pediatric weight-based doses, draw volumes, and field preparation — works offline.',
      icon: 'calculator',
      variant: 'reference',
      onClick: () => setView('drug-calculator'),
    },
    {
      id: 'fire-weather',
      title: 'Fire Weather',
      description: 'NWS fire-weather forecast for the phone location with offline cache.',
      icon: 'fire',
      variant: 'reference',
      onClick: () => setView('fire-weather'),
    },
  ];

  return (
    <>
      <AddToHomePrompt />
      <div className="app-container">
        <RemsHeader profile={profile} onSignOut={signOut} />

        <div className="dashboard-intro">
          <div className="dashboard-intro-copy">
            <span className="dashboard-eyebrow">REMS ACCESS</span>
            <h2>Field-ready resources in one place.</h2>
            <p>Gear accountability and field references organized for quick access from the station or the incident.</p>
          </div>
          <div className="dashboard-access-badge">{temporary ? 'Admin Verification Pending' : 'REMS access verified'}</div>
        </div>

        {temporary && (
          <section className="dashboard-section">
            <div className="tracking-card">
              <strong>Admin Verification Pending</strong>
              <p>Your temporary REMS access expires {expiryText(profile?.temporary_access_expires_at) || 'after 30 days'} unless a Program Administrator verifies it as permanent.</p>
            </div>
          </section>
        )}

        <section className="dashboard-section">
          <div className="section-heading">
            <div>
              <span className="section-kicker">FIELD OPERATIONS</span>
              <h2>Operational tools</h2>
              <p>The REMS tools you are most likely to need before, during and after an assignment.</p>
            </div>
          </div>
          <TileGrid tiles={operationalTiles} />
        </section>

        <ContactView sheetId={CONTACTS_SHEET_ID} sheetName="Contacts" />
      </div>
    </>
  );
}
